import { useState, useEffect } from 'react';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';

const DISMISSED_KEY = 'preminade_pwa_install_dismissed';

function isIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isStandalone = (window.navigator as any).standalone === true;
  const isSafari = /safari/i.test(ua) && !/crios|fxios|opios|mercury/i.test(ua);
  return isIOS && isSafari && !isStandalone;
}

function isStandalone() {
  return (window.navigator as any).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
}

export function PWAInstallBanner({ t }: { t: Theme }) {
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    if (isIOSSafari()) {
      setPlatform('ios');
      const timer = setTimeout(() => {
        setShow(true);
        setTimeout(() => setVisible(true), 50);
      }, 3000);
      return () => clearTimeout(timer);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('android');
      const timer = setTimeout(() => {
        setShow(true);
        setTimeout(() => setVisible(true), 50);
      }, 3000);
      return () => clearTimeout(timer);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => setShow(false), 300);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  const handleInstallAndroid = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') dismiss();
    setDeferredPrompt(null);
  };

  if (!show) return null;

  return (
    <>
      {/* Banner */}
      <div style={{
        position: 'fixed', bottom: 80, left: 16, right: 16, zIndex: 999,
        background: t.ink, borderRadius: 18, padding: '14px 16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 280ms ease, transform 280ms ease',
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: '#C4583C', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2"/>
            <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="3"/>
          </svg>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, color: t.paper, marginBottom: 3 }}>
            Add preminade to your home screen
          </div>
          {platform === 'ios' ? (
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>
              Tap <ShareIcon /> then <strong style={{ color: 'rgba(255,255,255,0.85)' }}>"Add to Home Screen"</strong>
              {' '}— or{' '}
              <button onClick={() => setShowModal(true)} style={{
                all: 'unset', cursor: 'pointer',
                color: '#E8A598', fontWeight: 600, textDecoration: 'underline',
              }}>see full instructions</button>
            </div>
          ) : (
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>
              Install the app for the best experience
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          {platform === 'android' && (
            <button onClick={handleInstallAndroid} style={{
              all: 'unset', cursor: 'pointer',
              padding: '5px 12px', borderRadius: 100,
              background: '#C8553D', color: 'white',
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
            }}>
              Install
            </button>
          )}
          {platform === 'ios' && (
            <button onClick={() => setShowModal(true)} style={{
              all: 'unset', cursor: 'pointer',
              padding: '5px 12px', borderRadius: 100,
              background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)',
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
            }}>
              How?
            </button>
          )}
          <button onClick={dismiss} style={{
            all: 'unset', cursor: 'pointer',
            width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.35)', fontSize: 18, lineHeight: 1,
          }}>×</button>
        </div>
      </div>

      {/* Full instructions modal */}
      {showModal && (
        <InstallModal t={t} platform={platform!} onClose={() => setShowModal(false)} onDismiss={dismiss} />
      )}
    </>
  );
}

function InstallModal({ t, platform, onClose, onDismiss }: {
  t: Theme; platform: 'ios' | 'android'; onClose: () => void; onDismiss: () => void;
}) {
  const iosSteps = [
    { icon: '1', text: 'Open preminade in Safari (not Chrome or Firefox)' },
    { icon: '2', text: 'Tap the Share button at the bottom of the screen' },
    { icon: '3', text: 'Scroll down and tap "Add to Home Screen"' },
    { icon: '4', text: 'Tap "Add" in the top right corner' },
    { icon: '5', text: 'Find preminade on your home screen and open it' },
  ];
  const androidSteps = [
    { icon: '1', text: 'Open preminade in Chrome' },
    { icon: '2', text: 'Tap the three-dot menu in the top right' },
    { icon: '3', text: 'Tap "Add to Home screen" or "Install app"' },
    { icon: '4', text: 'Tap "Add" or "Install" to confirm' },
    { icon: '5', text: 'Find preminade on your home screen and open it' },
  ];
  const steps = platform === 'ios' ? iosSteps : androidSteps;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        width: '100%', maxWidth: 430,
        background: t.card, borderRadius: '24px 24px 0 0',
        padding: '24px 24px 40px',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, lineHeight: 1.1 }}>
              Install preminade
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', color: t.inkMute, marginTop: 4 }}>
              {platform === 'ios' ? 'iPhone / iPad' : 'Android'}
            </div>
          </div>
          <button onClick={onClose} style={{
            all: 'unset', cursor: 'pointer', width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', background: t.paperAlt,
            fontFamily: FONTS.sans, fontSize: 20, color: t.inkMute,
          }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: t.terracotta, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: FONTS.mono, fontSize: 12, fontWeight: 600,
              }}>
                {step.icon}
              </div>
              <div style={{
                fontFamily: FONTS.sans, fontSize: 14, color: t.inkSoft,
                lineHeight: 1.45, paddingTop: 4,
              }}>
                {step.text}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: 24, padding: '12px 16px', borderRadius: 12,
          background: t.paperAlt, border: `1px solid ${t.line}`,
          fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, lineHeight: 1.5,
        }}>
          Once installed, preminade works like a native app — faster, offline-ready, and without the browser bar.
        </div>

        <button onClick={() => { onClose(); onDismiss(); }} style={{
          all: 'unset', cursor: 'pointer', display: 'block',
          width: '100%', marginTop: 16, textAlign: 'center',
          fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute,
        }}>
          Don't show this again
        </button>
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" style={{ display: 'inline', verticalAlign: 'middle', margin: '0 2px' }}>
      <path d="M6 1v8M3 3.5L6 1l3 2.5" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M1 6.5V12a1 1 0 001 1h8a1 1 0 001-1V6.5" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
